/**
 * Footer del sidebar contextual del Inbox WhatsApp — muestra avatar, nombre y email
 * del usuario (patrón Chatwoot). Al hacer click abre un popover con:
 *  - disponibilidad (online/away/offline) persistida en localStorage
 *  - atajos de teclado, ajustes del perfil, cambiar apariencia
 *  - consola SuperAdmin (solo si email está en SUPER_ADMIN_EMAILS)
 *  - cerrar sesión
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Text,
  Box,
  Popover,
  ActionList,
  InlineStack,
  BlockStack,
  Divider,
  Icon,
} from '@/lib/polaris';
import {
  KeyboardIcon,
  ProfileIcon,
  ThemeIcon,
  SettingsIcon,
  ExitIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@/lib/polaris-icons';
import { getAvatarColor, getInitials } from './wa-helpers';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes.config';
import { abrirApariencia } from '@/tema/apariencia';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { logError } from '@/lib/logger';

interface UserProfile {
  name: string;
  email: string | null;
  avatar_url: string | null;
}

type Availability = 'online' | 'away' | 'offline';

/**
 * Availability: fuente de verdad en DB (`staff_channel_roles.availability`).
 * Así el estado se sincroniza entre el UserMenu del sidebar y la tabla de
 * Agentes (vía Supabase Realtime), igual que Chatwoot/Intercom.
 * `sf_wa_auto_offline` sigue siendo preferencia local (no tiene sentido
 * sincronizarla — es del cliente actual).
 */
const AUTO_OFFLINE_KEY = 'sf_wa_auto_offline';
const AVAILABILITY_OPTIONS: Array<{ label: string; value: Availability }> = [
  { label: 'En línea', value: 'online' },
  { label: 'Ausente', value: 'away' },
  { label: 'Desconectado', value: 'offline' },
];
const AVAILABILITY_COLOR: Record<Availability, string> = {
  online: '#25D366',
  away: '#fbc02d',
  offline: '#9e9e9e',
};

function readAutoOffline(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(AUTO_OFFLINE_KEY) === '1';
}

/** Switch toggle minimalista sin dependencia externa. */
function SwitchToggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 32, height: 18, borderRadius: 9,
        background: checked ? '#25D366' : 'var(--p-color-bg-fill-tertiary)',
        border: 'none', cursor: 'pointer', padding: 0,
        transition: 'background 120ms ease',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 2, left: checked ? 16 : 2,
        width: 14, height: 14, borderRadius: '50%',
        background: '#fff',
        transition: 'left 120ms ease',
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }} />
    </button>
  );
}

export function WaInboxNavFooter() {
  const { user, tenantId } = useAuth();
  const storeNavigate = useStoreNavigate();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [availability, setAvailabilityState] = useState<Availability>('offline');
  const [autoOffline, setAutoOfflineState] = useState<boolean>(() => readAutoOffline());
  const [availExpanded, setAvailExpanded] = useState(false);

  // Availability desde DB — fuente de verdad compartida con la tabla de Agentes.
  // Realtime: si un supervisor cambia mi availability desde el dashboard, se
  // refleja aquí sin reload.
  useEffect(() => {
    if (!user?.id || !tenantId) return;
    let cancelled = false;

    (async () => {
      const { data } = await supabase
        .from('staff_channel_roles')
        .select('availability')
        .eq('tenant_id', tenantId)
        .eq('user_id', user.id)
        .eq('channel', 'whatsapp')
        .maybeSingle();
      if (cancelled) return;
      const row = data as { availability?: Availability } | null;
      if (row?.availability) setAvailabilityState(row.availability);
    })();

    const channel = supabase
      .channel(`wa-availability-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'staff_channel_roles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as { availability?: Availability; channel?: string; tenant_id?: string };
          if (row?.channel === 'whatsapp' && row?.tenant_id === tenantId && row.availability) {
            setAvailabilityState(row.availability);
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [user?.id, tenantId]);

  // Lo decide la base (`platform_admins`), no una lista de emails en el bundle.
  const isSuperAdmin = useIsSuperAdmin();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('name, email, avatar_url')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const metadata = user.user_metadata || {};
      setProfile({
        name: data?.name || metadata.full_name || metadata.name || user.email?.split('@')[0] || 'Usuario',
        email: data?.email || user.email || null,
        avatar_url: data?.avatar_url || metadata.avatar_url || null,
      });
    })();
    return () => { cancelled = true; };
  }, [user]);

  const setAvailability = useCallback(async (next: Availability) => {
    // UI optimista — revertimos si la DB falla.
    const prev = availability;
    setAvailabilityState(next);
    setAvailExpanded(false);
    if (!user?.id || !tenantId) return;
    const { error } = await supabase
      .from('staff_channel_roles')
      .update({ availability: next, updated_at: new Date().toISOString() })
      .eq('tenant_id', tenantId)
      .eq('user_id', user.id)
      .eq('channel', 'whatsapp');
    if (error) {
      logError('No se pudo actualizar disponibilidad WhatsApp', error);
      setAvailabilityState(prev);
    }
  }, [availability, tenantId, user?.id]);

  const toggleAutoOffline = useCallback((next: boolean) => {
    window.localStorage.setItem(AUTO_OFFLINE_KEY, next ? '1' : '0');
    setAutoOfflineState(next);
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/login';
    } catch (error) {
      logError('Error al cerrar sesión desde inbox nav', error);
    }
  }, []);

  if (!profile) return null;

  const dotColor = AVAILABILITY_COLOR[availability];
  const currentLabel = AVAILABILITY_OPTIONS.find((o) => o.value === availability)?.label ?? 'En línea';
  const initials = getInitials(profile.name, profile.email ?? '');
  const avatarBg = getAvatarColor(profile.name);

  const menuItems = [
    {
      content: 'Atajos de teclado',
      icon: KeyboardIcon,
      onAction: () => {
        setMenuOpen(false);
        // Dispara el Command Palette global (⌘K) que lista comandos y atajos.
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
      },
    },
    {
      content: 'Ajustes del perfil',
      icon: ProfileIcon,
      onAction: () => {
        setMenuOpen(false);
        storeNavigate(ROUTES.STORE_ACCOUNT);
      },
    },
    {
      content: 'Cambiar apariencia',
      icon: ThemeIcon,
      onAction: () => {
        setMenuOpen(false);
        abrirApariencia();
      },
    },
    ...(isSuperAdmin
      ? [{
          content: 'Consola SuperAdmin',
          icon: SettingsIcon,
          onAction: () => {
            setMenuOpen(false);
            navigate('/admin');
          },
        }]
      : []),
    {
      content: 'Cerrar sesión',
      icon: ExitIcon,
      destructive: true,
      onAction: () => {
        setMenuOpen(false);
        void handleSignOut();
      },
    },
  ];

  const activator = (
    <button
      type="button"
      className="wa-inbox-nav-footer__activator"
      onClick={() => { setMenuOpen((v) => !v); setAvailExpanded(false); }}
      aria-expanded={menuOpen}
      aria-haspopup="menu"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 12px',
        background: 'transparent',
        border: 'none',
        borderTop: '1px solid var(--p-color-border)',
        cursor: 'pointer',
        textAlign: 'left',
        minWidth: 0,
        transition: 'background 120ms ease, padding 200ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="wa-inbox-nav-footer__avatar-wrap" style={{ position: 'relative', flexShrink: 0 }}>
        <div className="wa-inbox-nav-footer__avatar" style={{
          width: 32, height: 32, borderRadius: '50%',
          background: profile.avatar_url ? 'transparent' : avatarBg,
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
          overflow: 'hidden',
          transition: 'width 200ms ease, height 200ms ease, font-size 200ms ease',
        }}>
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : initials}
        </div>
        <span
          aria-hidden="true"
          className="wa-inbox-nav-footer__avatar-dot"
          style={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: dotColor,
            border: '2px solid var(--p-color-bg-surface)',
            transition: 'width 200ms ease, height 200ms ease',
          }}
        />
      </div>
      <div className="wa-inbox-nav-footer__text" style={{ flex: 1, minWidth: 0 }}>
        <BlockStack gap="025">
          <Text as="p" variant="bodySm" fontWeight="semibold" truncate>
            {profile.name}
          </Text>
          {profile.email && (
            <Text as="p" variant="bodyXs" tone="subdued" truncate>
              {profile.email}
            </Text>
          )}
        </BlockStack>
      </div>
    </button>
  );

  return (
    <Box background="bg">
      <Popover
        active={menuOpen}
        activator={activator}
        onClose={() => { setMenuOpen(false); setAvailExpanded(false); }}
        preferredAlignment="left"
        preferredPosition="above"
        autofocusTarget="first-node"
      >
        <Popover.Pane>
          <Box padding="400">
            <BlockStack gap="300">
              <Text as="p" variant="bodySm" tone="subdued">
                Establecer su disponibilidad
              </Text>
              <div>
                <button
                  type="button"
                  onClick={() => setAvailExpanded((v) => !v)}
                  aria-expanded={availExpanded}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 6px 4px 10px', borderRadius: 999,
                    background: `${dotColor}1f`, color: 'var(--p-color-text)',
                    border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 500, lineHeight: 1.2,
                    transition: 'background 120ms ease',
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: dotColor, flexShrink: 0,
                  }} />
                  <span>{currentLabel}</span>
                  <span style={{ display: 'inline-flex', width: 14, height: 14 }}>
                    <Icon source={ChevronDownIcon} tone="subdued" />
                  </span>
                </button>
                {availExpanded && (
                  <div style={{
                    marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2,
                    background: 'var(--p-color-bg-surface)',
                    border: '1px solid var(--p-color-border-subdued)',
                    borderRadius: 8, padding: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}>
                    {AVAILABILITY_OPTIONS.map((opt) => {
                      const active = opt.value === availability;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setAvailability(opt.value)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 8px', borderRadius: 6,
                            background: active ? 'var(--p-color-bg-surface-selected)' : 'transparent',
                            border: 'none', cursor: 'pointer', textAlign: 'left',
                            fontSize: 13, color: 'var(--p-color-text)',
                            transition: 'background 120ms ease',
                          }}
                          onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                          onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: AVAILABILITY_COLOR[opt.value], flexShrink: 0,
                          }} />
                          <span style={{ flex: 1 }}>{opt.label}</span>
                          {active && (
                            <span style={{ display: 'inline-flex', width: 14, height: 14 }}>
                              <Icon source={CheckIcon} tone="subdued" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
                <Text as="span" variant="bodySm">
                  Marcar como desconectado automáticamente
                </Text>
                <SwitchToggle
                  checked={autoOffline}
                  onChange={toggleAutoOffline}
                  label="Marcar como desconectado automáticamente"
                />
              </InlineStack>
            </BlockStack>
          </Box>
          <Divider />
          <ActionList actionRole="menuitem" items={menuItems} />
        </Popover.Pane>
      </Popover>
    </Box>
  );
}
